import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Input, message, Steps } from 'antd';
import { QrcodeOutlined, SaveOutlined, SolutionOutlined, } from '@ant-design/icons';

import { requireActiveDeviceReturn, requireAddDeviceReturn, } from '../../redux/actions';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import regex from '../../constants/system.keys';
import InputTag from '../../components/InputTag';
import LocationSearchLocationIQ from '../../components/LocationSearchLocationIQ';
import { onlyLettersAndNumbers } from '../../helpers/groupByCoors';

const { Step } = Steps;
const layout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 20 },
};
const AddGatewayComponent = ({
  dispatch,
  onRequireActiveDevice,
  onAddDevice,
  addDeviceReturn,
  activeDeviceReturn,
  onUpdate,
  handleAddSuccessful,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [, forceUpdate] = useState();
  const [requestId, setRequestId] = useState(new Date().getTime());
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [serialInput, setSerialInput] = useState('');
  const [isBlock, setIsBlock] = useState(false);
  const [isLoad, setIsLoad] = useState(false);
  const serialRef = useRef(null);
  const [address, setAddress] = useState({
    note: '',
  });
  const [alertUpdate, setAlertUpdate] = useState({
    visible: false,
    type: null,
    message: null,
  });
  const { updateDevice } = useSelector((state) => state);
  const [updateReturn, setUpdateReturn] = useState();

  useEffect(() => {
    forceUpdate({});
    return () => {
      setIsLoading(false);
      setStep(0);
      setSerialInput('');
      setIsBlock(false);
      setIsLoad(false);
      setAddress({
        note: '',
      });
      setAlertUpdate({
        visible: false,
        type: null,
        message: null,
      });
      setUpdateReturn(null);
    };
  }, []);

  useEffect(() => {
    setIsLoading(false);
    if (
      addDeviceReturn &&
      (addDeviceReturn.result === -2 || addDeviceReturn.result === -3)
    ) {
      form.setFields([
        {
          name: 'serial',
          errors: [addDeviceReturn.message],
        },
      ]);
    }
    if (addDeviceReturn && addDeviceReturn.result === 1) {
      dispatch(requireActiveDeviceReturn(null));
      form.resetFields();
      return setStep(1);
    }
  }, [activeDeviceReturn, addDeviceReturn, dispatch, form, serialInput]);

  useEffect(() => {
    setIsLoading(false);
    if (activeDeviceReturn && activeDeviceReturn.result === -4) {
      form.setFields([
        {
          name: 'activationCode',
          errors: [activeDeviceReturn.message],
        },
      ]);
    }
    if (activeDeviceReturn && activeDeviceReturn.result === 1) {
      dispatch(requireAddDeviceReturn(null));
      form.setFieldsValue({
        lastSerial: serialInput,
      });
      return setStep(2);
    }
  }, [activeDeviceReturn, dispatch, form, serialInput]);

  const onFinish = useCallback(
    (values) => {
      setIsLoading(true);
      if (step === 0) {
        setSerialInput(values.serial);
        return onRequireActiveDevice(values.serial);
      }
      if (step === 1) {
        return onAddDevice(serialInput, values.activationCode);
      }
    },
    [onAddDevice, onRequireActiveDevice, serialInput, step]
  );

  /////////////////////////////////////////////////////////////

  useEffect(() => {
    if (updateDevice && updateDevice.requestId === requestId) {
      setUpdateReturn(updateDevice);
    }
  }, [updateDevice]);

  useEffect(() => {
    form.setFieldsValue({ serial: serialInput });
    return () => {
      setAlertUpdate({ visible: false });
      form.resetFields();
    };
  }, [form, serialInput]);

  useEffect(() => {
    if (serialRef.current && step === 0) {
      setTimeout(() => { serialRef.current.input.focus() }, 1)
    }
  }, [serialRef, step]);

  const onChangeStep = (step) => {
    form.resetFields();
    setStep((state) => (state > step ? step : state));
  };

  const handleAddressChange = (address) => {
    form.setFieldsValue({
      location: address,
    });
    setAddress((state) => ({
      ...state,
      note: address,
    }));
  };

  const handleSelect = (address) => {
    form.setFieldsValue({
      location: address?.note ?? '',
    });
    setAddress(address);
  };

  useEffect(() => {
    if (updateReturn && updateReturn.serial === serialInput) {
      setIsLoad(false);
      if (updateReturn.isSuccess) {
        setUpdateReturn(null);
        setStep(0);
        form.resetFields();
        handleAddSuccessful();
      } else {
        setAlertUpdate({
          visible: true,
          type: 'warning',
          message: t('Something went wrong, try again!!'),
        });
      }
    }
  }, [form, handleAddSuccessful, serialInput, t, updateReturn]);

  const handleOnUpdate = useCallback(
    (values) => {
      if (!values.tags || (values.tags && onlyLettersAndNumbers(values.tags?.join('')))) {
        if (requestId) {
          const data = {
            name: values.name,
            note: values.location ?? "",
            serial: serialInput,
            lat: address.lat ?? 0,
            lng: address.lng ?? 0,
            tags: values.tags?.join(';')?.toLowerCase() || ';',
          };
          const dataTimeRequest = new Date().getTime();
          setRequestId(dataTimeRequest)
          setIsLoad(true);
          return onUpdate(data, dataTimeRequest);
        }
      } else {
        message.warning(t('Please enter only letters and numbers!'));
      }
    },
    [address, onUpdate, requestId, serialInput]
  );

  if (step === 2) {
    return (
      <div className="edit-gateway-component">
        <div className="steps-add-gateways">
          <Steps
            size="small"
            current={step}
            onChange={onChangeStep}
            type="navigation"
            className="site-navigation-steps"
          >
            <Step title={t('Scan')} />
            <Step title={t('Activate')} />
            <Step title={t('Update')} />
          </Steps>
        </div>
        <Form
          form={form}
          {...layout}
          name="form_edit_gateway"
          onFinish={(values) => handleOnUpdate(values)}
        >
          <Form.Item
            name="name"
            label={t('Name')}
            rules={[
              {
                required: true,
                message: t('Please input the name of gateway!'),
              },
              {
                pattern: regex.isName,
                message: t('Please enter only letters and numbers!'),
              },
              {
                max: 80,
                message: t('Please input up to 80 characters!'),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="lastSerial" label={t('Serial')}>
            <Input disabled={true} />
          </Form.Item>

          <Form.Item
            name="location"
            rules={[
              {
                pattern: regex.isName,
                message: t('Please enter only letters and numbers!'),
              },
              {
                max: 200,
                message: t('Please input up to 200 characters!'),
              },
            ]}
            label={t('Location')}
          >
            <LocationSearchLocationIQ
              address={address.note}
              onChange={handleAddressChange}
              onAddressSelect={handleSelect}
              setIsBlock={setIsBlock}
            />
          </Form.Item>
          <Form.Item
            name="tags"
            className="tags-device"
            label={t('Tag')}
            style={{ borer: '1px solid #434343', borderRadius: '2px' }}
          >
            <InputTag numberTag={3} nameAdd="Add tag" />
          </Form.Item>
          <div className="edit-gateway-alert">
            {alertUpdate.visible && (
              <Alert
                {...alertUpdate}
                showIcon
                closable
                onClose={() => setAlertUpdate({ visible: false })}
              />
            )}
          </div>
          <div className="btn-update-on-add-gateway">
            <Button onClick={() => handleAddSuccessful()}>{t('Skip')}</Button>
            <Form.Item shouldUpdate>
              {() => (
                <Button
                  icon={<SaveOutlined />}
                  htmlType="submit"
                  type="primary"
                  loading={isLoad}
                  disabled={
                    form.getFieldsError().filter(({ errors }) => errors.length)
                      .length
                  }
                >
                  {t('Update')}
                </Button>
              )}
            </Form.Item>
          </div>
        </Form>
      </div>
    );
  }
  return (
    <div className="add-box-component">
      <div className="steps-add-gateways">
        <Steps
          size="small"
          current={step}
          onChange={onChangeStep}
          type="navigation"
          className="site-navigation-steps"
        >
          <Step title={t('Scan')} />
          <Step title={t('Activate')} />
          <Step title={t('Update')} />
        </Steps>
      </div>
      <div className="title-add-box">
        {step === 0
          ? t('Enter your device serial code, please find it behind device')
          : `${t('Enter the activation code for')} : ${serialInput}`}
      </div>
      <div className="main-form-add-box">
        <Form
          form={form}
          name="horizontal_login"
          layout="inline"
          onFinish={onFinish}
        >
          {step === 0 ? (
            <Form.Item
              name="serial"
              rules={[
                {
                  required: true,
                  message: t('Please enter the device serial!'),
                },
                {
                  pattern: regex.isName,
                  message: t('Please enter only letters and numbers!'),
                },
                {
                  max: 80,
                  message: t('Please input up to 80 characters!'),
                },
              ]}
            >
              <Input
                ref={serialRef}
                prefix={<QrcodeOutlined className="site-form-item-icon" />}
                placeholder={t('Device serial')}
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="activationCode"
              rules={[
                {
                  required: true,
                  message: t('Please enter the verification code'),
                },
                {
                  pattern: regex.isurl,
                  message: t('Please enter the verification code'),
                },
                {
                  max: 80,
                  message: t('Please input up to 80 characters!'),
                },
              ]}
            >
              <Input
                prefix={<SolutionOutlined className="site-form-item-icon" />}
                placeholder={t('Verification codes')}
              />
            </Form.Item>
          )}

          <Form.Item shouldUpdate>
            {() => (
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                disabled={
                  !form.isFieldsTouched(true) ||
                  !!form.getFieldsError().filter(({ errors }) => errors.length)
                    .length
                }
              >
                {t('Next')}
              </Button>
            )}
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default AddGatewayComponent;
